import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Clock,
  MapPin,
  Calendar,
  FileText,
  Users,
  Wallet,
  CheckCircle,
  XCircle,
  ExternalLink,
  Ticket,
  StickyNote,
  AlertTriangle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '../../lib/utils';
import { CATEGORIES } from './constants';

export const ActivityViewModal = ({
  open,
  onOpenChange,
  activity,
  members = [],
  payments = [],
  formatCurrency,
}) => {
  if (!activity) return null;

  const CategoryIcon = CATEGORIES[activity.category]?.icon || MapPin;
  const categoryColor = CATEGORIES[activity.category]?.color || 'bg-slate-500';
  const categoryLabel = CATEGORIES[activity.category]?.label || 'Outro';

  // Format date
  const activityDate = activity.date
    ? format(
        typeof activity.date === 'string' ? parseISO(activity.date) : activity.date,
        "dd 'de' MMMM 'de' yyyy (EEEE)",
        { locale: ptBR }
      )
    : null;

  // Get assigned member IDs
  const assignedMemberIds = activity.is_for_everyone
    ? members.map((m) => m.user_id)
    : activity.assigned_members || [];

  // Get linked payments for this activity
  const linkedPayments = payments.filter(
    (p) => p.itinerary_item_id === activity.id
  );

  // Calculate payment totals
  const totalPaymentAmount = linkedPayments.reduce(
    (sum, p) => sum + (p.total_amount || 0),
    0
  );
  const totalPaid = linkedPayments.reduce(
    (sum, p) => sum + (p.total_paid || 0),
    0
  );

  // Check for pending actions
  const hasMembersAssigned = activity.is_for_everyone || (activity.assigned_members && activity.assigned_members.length > 0);
  const needsPaymentSplit = !activity.is_free && activity.cost > 0 && linkedPayments.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] p-0 flex flex-col">
        {/* Header with category color */}
        <div className={cn('p-4 text-white shrink-0', categoryColor)}>
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <CategoryIcon className="w-5 h-5" />
              <Badge variant="secondary" className="bg-white/20 text-white border-0">
                {categoryLabel}
              </Badge>
              {activity.is_free && (
                <Badge className="bg-green-500/80 text-white border-0">
                  Gratuito
                </Badge>
              )}
            </div>
            <DialogTitle className="text-xl text-white">
              {activity.title}
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Pending Actions Alerts */}
            {(!hasMembersAssigned || needsPaymentSplit) && (
              <div className="space-y-2">
                {!hasMembersAssigned && (
                  <Alert className="bg-yellow-500/10 border-yellow-500/30">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    <AlertDescription className="text-yellow-200 text-sm ml-2">
                      <span className="font-medium">Membros não definidos:</span> Defina quem participará desta atividade.
                    </AlertDescription>
                  </Alert>
                )}
                {needsPaymentSplit && (
                  <Alert className="bg-orange-500/10 border-orange-500/30">
                    <Wallet className="h-4 w-4 text-orange-500" />
                    <AlertDescription className="text-orange-200 text-sm ml-2">
                      <span className="font-medium">Rateio pendente:</span> Gere o rateio de pagamento para esta atividade ({formatCurrency(activity.cost)}).
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Date & Time */}
            <div className="space-y-2">
              {activityDate && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Calendar className="w-4 h-4 text-orange-400" />
                  <span>{activityDate}</span>
                </div>
              )}
              {(activity.start_time || activity.end_time) && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Clock className="w-4 h-4 text-orange-400" />
                  <span>
                    {activity.start_time || '--:--'}
                    {activity.end_time && ` até ${activity.end_time}`}
                  </span>
                </div>
              )}
            </div>

            {/* Location & Address */}
            {(activity.location || activity.address) && (
              <div className="space-y-1">
                {activity.location && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <MapPin className="w-4 h-4 text-orange-400" />
                    <span>{activity.location}</span>
                  </div>
                )}
                {activity.address && (
                  <p className="text-sm text-slate-400 ml-6">{activity.address}</p>
                )}
              </div>
            )}

            {/* Description */}
            {activity.description && (
              <div className="bg-slate-800/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-400 mb-2">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">Descrição</span>
                </div>
                <p className="text-slate-300 text-sm whitespace-pre-wrap">
                  {activity.description}
                </p>
              </div>
            )}

            {/* Notes */}
            {activity.notes && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-yellow-400 mb-2">
                  <StickyNote className="w-4 h-4" />
                  <span className="text-sm font-medium">Observações</span>
                </div>
                <p className="text-slate-300 text-sm whitespace-pre-wrap">
                  {activity.notes}
                </p>
              </div>
            )}

            {/* Purchase Links */}
            {activity.purchase_links && activity.purchase_links.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-slate-400">
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-sm font-medium">Links úteis</span>
                </div>
                <div className="space-y-1 ml-6">
                  {activity.purchase_links.map((link, idx) => (
                    <a
                      key={idx}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm text-blue-400 hover:text-blue-300 underline truncate"
                    >
                      {link}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Tickets Info */}
            {activity.tickets_info && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-purple-400 mb-2">
                  <Ticket className="w-4 h-4" />
                  <span className="text-sm font-medium">Ingressos/Reservas</span>
                </div>
                <p className="text-slate-300 text-sm whitespace-pre-wrap">
                  {activity.tickets_info}
                </p>
              </div>
            )}

            <Separator className="bg-slate-700" />

            {/* Cost & Payments */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-400">
                <Wallet className="w-4 h-4" />
                <span className="text-sm font-medium">Custo e Pagamentos</span>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Custo da atividade:</span>
                  <span className="text-white font-medium">
                    {activity.is_free ? 'Gratuito' : formatCurrency(activity.cost || 0)}
                  </span>
                </div>

                {linkedPayments.length > 0 && (
                  <>
                    <Separator className="bg-slate-700" />
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-sm">Total rateado:</span>
                      <span className="text-orange-400 font-medium">
                        {formatCurrency(totalPaymentAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-sm">Total pago:</span>
                      <span className="text-green-400 font-medium">
                        {formatCurrency(totalPaid)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-sm">Pendente:</span>
                      <span className="text-yellow-400 font-medium">
                        {formatCurrency(totalPaymentAmount - totalPaid)}
                      </span>
                    </div>
                  </>
                )}

                {linkedPayments.length === 0 && !activity.is_free && activity.cost > 0 && (
                  <p className="text-xs text-slate-500 italic">
                    Nenhum pagamento gerado ainda
                  </p>
                )}
              </div>
            </div>

            <Separator className="bg-slate-700" />

            {/* Members */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-400">
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">Participantes</span>
                </div>
                <Badge variant="outline" className="text-slate-400 border-slate-600">
                  {assignedMemberIds.length} / {members.length}
                </Badge>
              </div>

              <div className="bg-slate-800/50 rounded-lg divide-y divide-slate-700">
                {members.map((member) => {
                  const isParticipating = assignedMemberIds.includes(member.user_id);
                  
                  // Find member payment if exists
                  const memberPayment = linkedPayments
                    .flatMap((p) => p.member_payments || [])
                    .find((mp) => mp.user_id === member.user_id);

                  return (
                    <div
                      key={member.user_id}
                      className={cn(
                        'flex items-center justify-between p-3 transition-colors',
                        !isParticipating && 'opacity-50'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                            isParticipating
                              ? 'bg-orange-500/20 text-orange-400'
                              : 'bg-slate-700 text-slate-500'
                          )}
                        >
                          {member.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm text-white">{member.user_name}</p>
                          {memberPayment && (
                            <p className="text-xs text-slate-400">
                              {memberPayment.is_paid ? (
                                <span className="text-green-400">
                                  Pago: {formatCurrency(memberPayment.amount_paid || 0)}
                                </span>
                              ) : (
                                <span className="text-yellow-400">
                                  Deve: {formatCurrency(memberPayment.amount_due || 0)}
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {isParticipating ? (
                          <Badge className="bg-green-500/20 text-green-400 border-0 gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Vai
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-700 text-slate-400 border-0 gap-1">
                            <XCircle className="w-3 h-3" />
                            Não vai
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
      </DialogContent>
    </Dialog>
  );
};
